import styled from 'styled-components';
import { U21Avatar } from 'app/shared/u21-ui/components/display/avatar/U21Avatar';
import { U21Divider } from 'app/shared/u21-ui/components/display/U21Divider';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21Tooltip } from 'app/shared/u21-ui/components/display/U21Tooltip';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import { U21AiChatMessageBubble } from 'app/shared/u21-ui/components/input/chat/components/bubbles/U21AiChatMessageBubble';
import { U21AiChatToolCallList } from 'app/shared/u21-ui/components/input/chat/components/U21AiChatToolCallList';
import { U21AiChatWidgetBubble } from 'app/shared/u21-ui/components/input/chat/components/bubbles/U21AiChatWidgetBubble';
import { CANCELLED_MESSAGE_TEXT } from 'app/shared/u21-ui/components/input/chat/constants';
import { U21AiChatMessage } from 'app/shared/u21-ui/components/input/chat/types';

interface Props {
  agentName: string;
  agentPicture?: string;
  disabled?: boolean;
  formattedTimestamp: string;
  hasAgentResponseBelow: boolean;
  message: U21AiChatMessage;
}

export const U21AiChatRow = ({
  agentName,
  agentPicture,
  disabled = false,
  formattedTimestamp,
  hasAgentResponseBelow,
  message,
}: Props) => {
  if (message.widget) {
    return (
      <UserRow align="end" horizontal spacing={1}>
        <StyledWidgetBubble
          {...message.widget}
          disabled={disabled || message.widget.disabled}
        />
        <U21Tooltip placement="left" tooltip={message.senderName}>
          <U21Avatar
            name={message.senderName}
            size="small"
            src={message.senderPicture || undefined}
          />
        </U21Tooltip>
      </UserRow>
    );
  }

  if (message.sender === 'agent') {
    return (
      <AgentRow align="end" horizontal>
        <U21Avatar
          name={message.senderName}
          size="small"
          src={message.senderPicture || undefined}
        />
        <StyledAgentMessageBubble
          formattedTimestamp={formattedTimestamp}
          message={message}
        />
      </AgentRow>
    );
  }

  return (
    <RowSpacer spacing={2}>
      <UserRow align="end" horizontal spacing={1}>
        <U21AiChatMessageBubble
          formattedTimestamp={formattedTimestamp}
          message={message}
        />
        <U21Tooltip placement="left" tooltip={message.senderName}>
          <U21Avatar
            name={message.senderName}
            size="small"
            src={message.senderPicture || undefined}
          />
        </U21Tooltip>
      </UserRow>
      {/* toolUses live on the user message that triggered them, but render
          left-aligned as agent work (tool calls the agent ran before replying) */}
      {message.toolUses && message.toolUses.length > 0 && (
        <AgentBlockRow align="end" horizontal spacing={1}>
          <AvatarColumn>
            {!hasAgentResponseBelow && (
              <U21Tooltip placement="right" tooltip={agentName}>
                <U21Avatar
                  name={agentName}
                  size="small"
                  src={agentPicture || undefined}
                />
              </U21Tooltip>
            )}
          </AvatarColumn>
          <U21AiChatToolCallList toolUses={message.toolUses} />
        </AgentBlockRow>
      )}
      {message.status === 'cancelled' && (
        <CancelledFooter>
          <U21Divider horizontal>
            <U21Typography color="text.secondary" variant="caption">
              {CANCELLED_MESSAGE_TEXT}
            </U21Typography>
          </U21Divider>
        </CancelledFooter>
      )}
    </RowSpacer>
  );
};

const RowSpacer = styled(U21Spacer)`
  max-width: 100%;
  min-width: 0;
  width: 100%;
`;

const AgentRow = styled(U21Spacer)`
  min-width: 0;
`;

const UserRow = styled(U21Spacer)`
  justify-content: flex-end;
  max-width: 100%;
  min-width: 0;
  width: 100%;
`;

const AgentBlockRow = styled(U21Spacer)`
  max-width: 60%;
  min-width: 0;
`;

const AvatarColumn = styled.div`
  align-items: flex-end;
  display: flex;
  flex-shrink: 0;
  height: ${({ theme }) => theme.spacing(2.5)};
  justify-content: center;
  width: ${({ theme }) => theme.spacing(2.5)};
`;

const StyledAgentMessageBubble = styled(U21AiChatMessageBubble)`
  max-width: 60%;
`;

const StyledWidgetBubble = styled(U21AiChatWidgetBubble)`
  max-width: 60%;
`;

const CancelledFooter = styled.div`
  padding-left: ${({ theme }) => theme.spacing(4)};
  width: 100%;
`;
